import { API } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useOutletContext } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Check, Crown, Zap } from 'lucide-react';
import { toast } from 'sonner';


export default function Subscription() {
  const { user } = useOutletContext();
  const [loading, setLoading] = useState(false);
  const [processingPlan, setProcessingPlan] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Check if returning from payment
    const params = new URLSearchParams(location.search);
    const sessionId = params.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [location]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 5;
    if (attempts >= maxAttempts) {
      toast.error('Payment verification timed out. Please contact support.');
      return;
    }

    try {
      const response = await fetch(`${API}/subscription/status/${sessionId}`, {
        credentials: 'include'
      });

      if (!response.ok) throw new Error('Failed to check status');

      const data = await response.json();

      if (data.status === 'success' || data.status === 'already_processed') {
        toast.success('Subscription activated! Welcome to Premium!');
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 1500);
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (error) {
      toast.error('Error verifying payment');
    }
  };

  const handleSubscribe = async (plan) => {
    setLoading(true);
    setProcessingPlan(plan);

    try {
      const originUrl = window.location.origin;
      const response = await fetch(`${API}/subscription/create-checkout`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          plan,
          origin_url: originUrl
        })
      });

      if (!response.ok) throw new Error('Failed to create checkout');

      const data = await response.json();
      window.location.href = data.url;
    } catch (error) {
      toast.error('Failed to start checkout');
      setLoading(false);
      setProcessingPlan(null);
    }
  };

  const isPremium = user?.subscription_status && user.subscription_status !== 'free';

  return (
    <div className="p-4 md:p-8 max-w-6xl mx-auto" data-testid="subscription-page">
      <div className="text-center mb-12">
        <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-4">
          Unlock Full Access
        </h1>
        <p className="text-muted-foreground text-lg">
          Upgrade to Premium for unlimited custom peptides and advanced features
        </p>
      </div>

      {isPremium && (
        <div className="mb-8 p-6 rounded-lg bg-success/10 border border-success/30 text-center">
          <Crown className="h-12 w-12 text-success mx-auto mb-3" />
          <h3 className="text-xl font-bold text-success mb-2">You're Premium!</h3>
          <p className="text-muted-foreground">
            Subscription Status: {user.subscription_status}
            {user.subscription_expires && (
              <span className="block mt-1">
                Expires: {new Date(user.subscription_expires).toLocaleDateString()}
              </span>
            )}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Monthly Plan */}
        <Card className="p-8 glass-card relative hover:-translate-y-2 transition-all duration-200">
          <div className="text-center mb-8">
            <Zap className="h-12 w-12 text-primary mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2">Monthly</h3>
            <div className="flex items-baseline justify-center mb-4">
              <span className="text-5xl font-bold">£4.99</span>
              <span className="text-muted-foreground ml-2">/month</span>
            </div>
            <p className="text-muted-foreground">Perfect for trying Premium</p>
          </div>

          <ul className="space-y-4 mb-8">
            <li className="flex items-start">
              <Check className="h-5 w-5 text-success mr-3 mt-0.5 flex-shrink-0" />
              <span>Unlimited custom peptides</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-success mr-3 mt-0.5 flex-shrink-0" />
              <span>Advanced calculator features</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-success mr-3 mt-0.5 flex-shrink-0" />
              <span>Unlimited protocols & stacks</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-success mr-3 mt-0.5 flex-shrink-0" />
              <span>Full inventory tracking</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-success mr-3 mt-0.5 flex-shrink-0" />
              <span>Export logs & data</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-success mr-3 mt-0.5 flex-shrink-0" />
              <span>Priority support</span>
            </li>
          </ul>

          <Button
            onClick={() => handleSubscribe('monthly')}
            disabled={loading || isPremium}
            className="w-full"
            size="lg"
            data-testid="subscribe-monthly-button"
          >
            {processingPlan === 'monthly' ? 'Processing...' : isPremium ? 'Active' : 'Subscribe Monthly'}
          </Button>
        </Card>

        {/* Annual Plan */}
        <Card className="p-8 glass-card relative hover:-translate-y-2 transition-all duration-200 border-2 border-primary">
          <div className="absolute top-4 right-4 bg-primary text-primary-foreground px-3 py-1 rounded-full text-xs font-bold">
            BEST VALUE
          </div>

          <div className="text-center mb-8">
            <Crown className="h-12 w-12 text-primary mx-auto mb-4" />
            <h3 className="text-2xl font-bold mb-2">Annual</h3>
            <div className="flex items-baseline justify-center mb-2">
              <span className="text-5xl font-bold">£50</span>
              <span className="text-muted-foreground ml-2">/year</span>
            </div>
            <p className="text-success font-medium mb-4">Save £9.88 (16% off)</p>
            <p className="text-muted-foreground">Best for serious researchers</p>
          </div>

          <ul className="space-y-4 mb-8">
            <li className="flex items-start">
              <Check className="h-5 w-5 text-success mr-3 mt-0.5 flex-shrink-0" />
              <span className="font-medium">Everything in Monthly, plus:</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-success mr-3 mt-0.5 flex-shrink-0" />
              <span>16% discount (2 months free)</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-success mr-3 mt-0.5 flex-shrink-0" />
              <span>Priority feature requests</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-success mr-3 mt-0.5 flex-shrink-0" />
              <span>Early access to new features</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-success mr-3 mt-0.5 flex-shrink-0" />
              <span>Dedicated support channel</span>
            </li>
            <li className="flex items-start">
              <Check className="h-5 w-5 text-success mr-3 mt-0.5 flex-shrink-0" />
              <span>Annual research reports</span>
            </li>
          </ul>

          <Button
            onClick={() => handleSubscribe('annual')}
            disabled={loading || isPremium}
            className="w-full"
            size="lg"
            data-testid="subscribe-annual-button"
          >
            {processingPlan === 'annual' ? 'Processing...' : isPremium ? 'Active' : 'Subscribe Annually'}
          </Button>
        </Card>
      </div>

      {/* Free Plan Info */}
      <Card className="mt-8 p-6 glass-card">
        <h3 className="text-xl font-bold mb-4">Free Plan Features</h3>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-3 text-muted-foreground">
          <li className="flex items-center">
            <Check className="h-4 w-4 text-muted-foreground mr-2" />
            Access to 20+ pre-loaded peptides
          </li>
          <li className="flex items-center">
            <Check className="h-4 w-4 text-muted-foreground mr-2" />
            1 custom peptide
          </li>
          <li className="flex items-center">
            <Check className="h-4 w-4 text-muted-foreground mr-2" />
            Basic calculator
          </li>
          <li className="flex items-center">
            <Check className="h-4 w-4 text-muted-foreground mr-2" />
            Supplier directory
          </li>
        </ul>
      </Card>
    </div>
  );
}
